import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust the path to your Firebase config file

const useAdminNotifications = (user) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      // Step 1: Fetch existing notifications from the top-level adminLeaveNotifications collection
      const existingNotificationsSnapshot = await getDocs(
        collection(db, 'adminLeaveNotifications')
      );

      const existingNotifications = new Set(
        existingNotificationsSnapshot.docs.map((doc) => doc.data().employeeId)
      );

      // Step 2: Query Firestore for pending leave requests
      const q = query(
        collection(db, 'employeeLeave'),
        where('status', '==', 'Pending')
      );

      const unsubscribe = onSnapshot(q, async (snapshot) => {
        const newNotifications = [];

        for (const docSnapshot of snapshot.docs) {
          const data = docSnapshot.data();
          const { employeeId, startDate, endDate } = data;

          // Check if this employeeId has already been processed
          if (!existingNotifications.has(employeeId)) {
            try {
              // Fetch the employee name from the employees collection
              const employeeDoc = await getDoc(doc(db, 'employees', employeeId));
              const employeeName = employeeDoc.exists() ? employeeDoc.data().name : 'Unknown';

              // Prepare the notification
              newNotifications.push({
                employeeId,
                employeeName,
                leaveDescription: `Leave request from ${employeeName} (ID: ${employeeId}) for ${startDate} to ${endDate}`,
                timestamp: data.timestamp, // Include timestamp for saving
              });
            } catch (error) {
              console.error('Error fetching employee data:', error);
            }
          }
        }

        // Step 3: Save new notifications to the top-level adminLeaveNotifications collection
        for (const notification of newNotifications) {
          try {
            await addDoc(collection(db, 'adminLeaveNotifications'), notification);
          } catch (error) {
            console.error('Error saving notification:', error);
          }
        }

        // Update the notifications and unread count state
        setNotifications(newNotifications.map((n) => n.leaveDescription));
        setUnreadCount(newNotifications.length);
      });

      // Clean up the listener on unmount
      return () => unsubscribe();
    };

    fetchNotifications();
  }, []);

  // Function to mark notifications as read
  const markNotificationsAsRead = () => {
    setUnreadCount(0);
  };

  return {
    notifications,
    unreadCount,
    markNotificationsAsRead,
  };
};

export default useAdminNotifications;
